/**
 * Display-only version of the "brand color panel" SRP job card (HitCompactV2.tsx). Used
 * wherever a read-only equivalent is needed (e.g. the company profile page). Same DOM classes
 * as hit-card-v2.css; interactive-only affordances (save/share/menu, filter links, login-prompt
 * donut) render as inert equivalents.
 *
 * Strings come from staticHitStrings when provided (for callers with no IntlProvider),
 * otherwise from react-intl.
 */
import React from 'react';
import { useIntl } from 'react-intl';
import { ShareNetwork, CheckCircle, Buildings, BellSimple } from '@phosphor-icons/react';
import { Icon } from '../../design-system/icons/Icon';
import { Link } from '../Link';
import { fixMojibake } from '../../utils/text/fix-mojibake';
import { CompanyLogo } from '../CompanyLogo';
import { CompanyName } from '../CompanyName';
import { MatchDonut } from '../MatchDonut';
import { adjustBrandColor } from '../../utils/brand-color/adjust-brand-color';
import { SalaryInferenceExplainer } from '../SalaryInferenceExplainer';
import { formatSalaryInferenceExplanation } from '../../utils/job-detail/salary-inference-explanation-intl';
import { JD_MORE_INFO_CUSTOM_TRIGGER_BARE } from '../../utils/job-detail/job-detail-more-info-affordance';
import { formatSrpSalaryParts } from '../../utils/data/format-job-card-labels';
import type { SalaryInferenceExplanationKind } from '../../utils/job-detail/resolve-salary-inference-explanation-kind';
import { shouldLinkCompanyProfile } from '../../utils/company/should-link-company-profile';
import { normalizeToStringArray } from '../../utils/data/normalize-to-string-array';
import { getStateLabelClass } from '../application-state/state-classes';
import type { HitCompactViewProps } from './HitCompactViewProps';
import type { MatchScoreBreakdown } from '../../utils/data/calculate-job-match-score';
import { useFormatMessage } from '../../i18n-review/client/Msg';

interface HitCompactV2ViewStrings {
  monthly: string;
  estMonthly: string;
  viewJob: string;
  salaryNotSpecified: string;
  /** Resolved explanation for the current salary inference kind ('' hides the explainer). */
  salaryInferenceExplanation: string;
  /** `{company}` already substituted. */
  companyLinkTitle: string;
}

function salaryInferenceTextFromStatic(
  kind: SalaryInferenceExplanationKind | null | undefined,
  s: NonNullable<HitCompactViewProps['staticHitStrings']>,
): string {
  if (!kind) return '';
  if (kind === 'market_median_from_similar_jobs') {
    return s.salaryInferenceMarketMedian ?? '';
  }
  return s.salaryInferenceUnverified ?? '';
}

export function HitCompactV2View(props: HitCompactViewProps): React.ReactElement {
  if (props.staticHitStrings) {
    return <HitCompactV2ViewStatic {...props} staticHitStrings={props.staticHitStrings} />;
  }
  return <HitCompactV2ViewWithIntl {...props} />;
}

function HitCompactV2ViewStatic(
  props: HitCompactViewProps & { staticHitStrings: NonNullable<HitCompactViewProps['staticHitStrings']> },
): React.ReactElement {
  const s = props.staticHitStrings;
  const companyDisplay = props.companyNameDisplay ?? props.companyId ?? '';
  const companyLinkTitleTpl =
    s.hitCompanyLinkTitle ?? s.hitCompanyProfileAria ?? 'Open company profile — data and insights for {company}';
  const strings: HitCompactV2ViewStrings = {
    monthly: s.hitV2Monthly ?? 'Monthly',
    estMonthly: s.hitV2EstMonthly ?? 'Est. Monthly',
    viewJob: s.hitV2ViewJob ?? 'View job',
    salaryNotSpecified: s.salaryNotSpecified,
    salaryInferenceExplanation: salaryInferenceTextFromStatic(props.salaryInferenceExplanationKind, s),
    companyLinkTitle: companyLinkTitleTpl.replace(/\{company\}/g, companyDisplay),
  };
  return <HitCompactV2ViewMarkup {...props} strings={strings} matchDonutStrings={s.matchDonut} />;
}

function HitCompactV2ViewWithIntl(props: HitCompactViewProps): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const companyDisplay = props.companyNameDisplay ?? props.companyId ?? '';
  // Static SSR shell has no matchScore yet (see buildHitCompactViewProps.ts) for anonymous
  // visitors, so pass MatchDonut its `strings` prop to render the same "?" placeholder the
  // interactive MatchDonutWithLoginPrompt shows — avoids swapping a plain skeleton circle for
  // the real donut treatment once the client mounts.
  const matchDonutStrings = {
    // MatchDonutWithStrings does its own `.replace('{score}', ...)` on this template, so we pass
    // the literal token through `formatMessage` as the {score} value rather than a real number.
    ariaLabelTemplate: formatMessage({ id: 'matchDonut.ariaLabel', defaultMessage: 'Match score: {score}%', description: 'Accessibility label for match donut chart' }, { score: '{score}' }),
    breakdownTitle: formatMessage({ id: 'matchDonut.breakdown.title', defaultMessage: 'Match Score Breakdown', description: 'Title for match score breakdown tooltip' }),
    breakdownSalary: formatMessage({ id: 'matchDonut.breakdown.salary', defaultMessage: 'Salary', description: 'Salary label in breakdown' }),
    breakdownVacation: formatMessage({ id: 'matchDonut.breakdown.vacation', defaultMessage: 'Vacation', description: 'Vacation label in breakdown' }),
    breakdownBenefits: formatMessage({ id: 'matchDonut.breakdown.benefits', defaultMessage: 'Benefits', description: 'Benefits label in breakdown' }),
    breakdownRemoteness: formatMessage({ id: 'matchDonut.breakdown.remoteness', defaultMessage: 'Remoteness', description: 'Remoteness label in breakdown' }),
    breakdownLanguages: formatMessage({ id: 'matchDonut.breakdown.languages', defaultMessage: 'Languages', description: 'Languages label in breakdown' }),
    loginPromptAria: formatMessage({ id: 'matchDonut.loginPromptAria', defaultMessage: 'Log in to see your match score', description: 'Accessibility label for login prompt' }),
  };
  const strings: HitCompactV2ViewStrings = {
    monthly: formatMessage({ id: 'hitV2.monthly', defaultMessage: 'Monthly', description: 'Salary period label on the redesigned job card panel' }),
    estMonthly: formatMessage({ id: 'hitV2.estMonthly', defaultMessage: 'Est. Monthly', description: 'Salary period label on the redesigned job card panel, estimated figure' }),
    viewJob: formatMessage({ id: 'hitV2.viewJob', defaultMessage: 'View job', description: 'Primary CTA button on the redesigned job card' }),
    salaryNotSpecified: formatMessage({ id: 'hit.salaryNotSpecified.card', defaultMessage: 'Salary not specified', description: 'Shown when salary is not specified in job card' }),
    salaryInferenceExplanation: formatSalaryInferenceExplanation(
      props.salaryInferenceExplanationKind ?? null,
      intl.formatMessage,
    ),
    companyLinkTitle: formatMessage(
      { id: 'hit.companyLinkTitle', defaultMessage: 'Open company profile — data and insights for {company}', description: 'Tooltip for the employer logo and name link on a job card' },
      { company: companyDisplay },
    ),
  };
  return <HitCompactV2ViewMarkup {...props} strings={strings} matchDonutStrings={matchDonutStrings} />;
}

function HitCompactV2ViewMarkup(
  props: HitCompactViewProps & {
    strings: HitCompactV2ViewStrings;
    matchDonutStrings?: NonNullable<HitCompactViewProps['staticHitStrings']>['matchDonut'];
  },
): React.ReactElement {
  const { strings } = props;
  const adjustedBrand = adjustBrandColor(props.brandColour);
  const requiredLanguageTags = normalizeToStringArray(props.requiredLanguages);
  const canLinkCompanyProfile =
    Boolean(props.companyProfileUrl) &&
    shouldLinkCompanyProfile({ companyId: props.companyId, isTyomarkkinatori: props.isTyomarkkinatori });
  const companyDisplay = props.companyNameDisplay ?? props.companyId ?? '';
  const isEstimate = props.salaryIsEstimate === true;
  const isSaved = props.isSaved === true;
  const isApplied = props.isApplied === true;
  const applicationStateSelector = props.staticHitStrings?.applicationStateSelector;
  const applicationStateRow =
    props.isToolsPage && applicationStateSelector ? (
      <div className="hit-state-row">
        <label className="hit-state-label">{applicationStateSelector.label}</label>
        <select
          value={props.currentApplicationState ?? ''}
          disabled
          aria-readonly
          className={`text-xs font-medium px-2.5 py-1.5 rounded border transition-all duration-200 appearance-none bg-white dark:bg-gray-800 cursor-default ${getStateLabelClass(props.currentApplicationState, isApplied)}`}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 0.25rem center',
            backgroundSize: '1em 1em',
            paddingRight: '1.75rem',
          }}
        >
          <option value="">{applicationStateSelector.none}</option>
          <option value="interviewing">{applicationStateSelector.interviewing}</option>
          <option value="offer">{applicationStateSelector.offer}</option>
          <option value="rejected">{applicationStateSelector.rejected}</option>
          <option value="hired">{applicationStateSelector.hired}</option>
        </select>
      </div>
    ) : null;

  const salaryParts = formatSrpSalaryParts({ salaryRangeMin: props.salaryRangeMin, salaryRangeMax: props.salaryRangeMax, locale: props.locale });
  const hasSalary = salaryParts.length > 0;
  // Defensive fallback for a caller that hasn't been updated to pass salaryRangeMin/Max yet —
  // render the already-formatted label as-is rather than guessing at a split.
  const fallbackLabel = !hasSalary ? props.salaryLabel.trim() : '';
  const hasExplanation = Boolean(props.salaryInferenceExplanationKind) && strings.salaryInferenceExplanation.trim().length > 0;
  /** A quote pulled straight from the listing text — no inference needed to explain it (see resolveSalaryInferenceExplanationKind). */
  const isVerifiedSalary = hasSalary && !props.salaryInferenceExplanationKind;
  const unitLabel = isEstimate ? strings.estMonthly : strings.monthly;

  const salaryChip = hasSalary ? (
    <SalaryInferenceExplainer explanationText={strings.salaryInferenceExplanation} triggerClassName={JD_MORE_INFO_CUSTOM_TRIGGER_BARE}>
      <span className={`hit-card-v2__salary-chip ${isVerifiedSalary ? 'hit-card-v2__salary-chip--verified' : ''}`}>
        {isVerifiedSalary ? <CheckCircle weight="fill" className="hit-card-v2__salary-chip-icon" /> : null}
        {salaryParts.map((amt, i) => (
          <span className="hit-card-v2__salary-chip-amt" key={i}>{i > 0 ? `- ${amt}` : amt}</span>
        ))}
        <span className="hit-card-v2__salary-chip-unit">
          {unitLabel}
          {hasExplanation ? <span className="hit-card-v2__salary-info-dot" aria-hidden="true">i</span> : null}
        </span>
      </span>
    </SalaryInferenceExplainer>
  ) : (
    <span className="hit-card-v2__salary-not-specified">{fallbackLabel || strings.salaryNotSpecified}</span>
  );

  const logo = (
    <CompanyLogo
      companyName={props.companyId || ''}
      initialsSourceName={companyDisplay || props.companyId}
      registryCompanyFormDescription={props.registryCompanyFormDescription}
      isTyomarkkinatori={props.isTyomarkkinatori}
      hostedLogoUrl={props.hostedLogoUrl}
      size={96}
      loading={(props.index ?? 0) < 3 ? 'eager' : 'lazy'}
    />
  );

  const metaFields: { key: string; label: string }[] = [];
  if (props.remoteness?.trim()) metaFields.push({ key: 'remote', label: fixMojibake(props.remoteness) });
  if (props.jobType?.trim()) metaFields.push({ key: 'jobType', label: fixMojibake(props.jobType) });
  if (props.normalisedRoleLevel?.trim()) metaFields.push({ key: 'roleLevel', label: fixMojibake(props.normalisedRoleLevel) });
  if (requiredLanguageTags.length > 0) metaFields.push({ key: 'language', label: requiredLanguageTags.map(fixMojibake).join(', ') });

  return (
    <article
      id={props.articleId}
      className="hit-card-v2"
      title={props.jobTitle}
      aria-labelledby={props.titleId}
    >
      <div className="hit-card-v2__panel" style={{ background: adjustedBrand.panel, color: adjustedBrand.on }}>
        {canLinkCompanyProfile && props.companyProfileUrl ? (
          <Link href={props.companyProfileUrl} locale={props.locale} className="hit-card-v2__logo-tile" title={strings.companyLinkTitle}>
            {logo}
          </Link>
        ) : (
          <div className="hit-card-v2__logo-tile">{logo}</div>
        )}
        {props.singleLocation.display ? (
          <div className="hit-card-v2__panel-location">
            <span className="hit-card-v2__panel-location-text">{fixMojibake(props.singleLocation.display)}</span>
          </div>
        ) : null}
        {props.companyId || props.lastSeenLabel ? (
          <div className="hit-card-v2__panel-footer">
            {props.companyId ? (
              <div className="hit-card-v2__panel-follow">
                <div
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors border-white/40 bg-white/15 text-white"
                  aria-hidden
                >
                  <BellSimple className="w-4 h-4" weight="bold" aria-hidden />
                </div>
              </div>
            ) : null}
            {props.lastSeenLabel ? <div className="hit-card-v2__posted">{props.lastSeenLabel}</div> : null}
          </div>
        ) : null}
      </div>

      <div className="hit-card-v2__content">
        <div className="hit-card-v2__top-row">
          <div className="hit-card-v2__company-block">
            {canLinkCompanyProfile && props.companyProfileUrl ? (
              <Link href={props.companyProfileUrl} locale={props.locale} className="hit-card-v2__company-name" title={strings.companyLinkTitle}>
                <span className="hit-card-v2__company-name-text">
                  <CompanyName name={companyDisplay} />
                </span>
                <Buildings weight="fill" className="hit-card-v2__company-name-icon" />
              </Link>
            ) : (
              <span className="hit-card-v2__company-name">
                <span className="hit-card-v2__company-name-text">
                  <CompanyName name={companyDisplay} />
                </span>
              </span>
            )}
          </div>
          <div className="hit-card-v2__actions">
            {props.matchDonutStrings ? (
              <MatchDonut
                score={typeof props.matchScore === 'number' && Number.isFinite(props.matchScore) ? props.matchScore : null}
                size={26}
                breakdown={(props.matchScoreData as MatchScoreBreakdown | undefined) ?? undefined}
                strings={props.matchDonutStrings}
                ringColorOverride={adjustedBrand.panel}
                labelColorOverride={{ light: '#16150F', dark: '#F3EFE4' }}
              />
            ) : typeof props.matchScore === 'number' && Number.isFinite(props.matchScore) ? (
              <MatchDonut
                score={props.matchScore}
                size={26}
                breakdown={(props.matchScoreData as MatchScoreBreakdown | undefined) ?? undefined}
                ringColorOverride={adjustedBrand.panel}
                labelColorOverride={{ light: '#16150F', dark: '#F3EFE4' }}
              />
            ) : (
              <div className="h-[26px] w-[26px] rounded-full bg-jc-skeleton/80" aria-hidden />
            )}
            <div className={`hit-card-v2__icon-btn ${isSaved ? 'hit-card-v2__icon-btn--saved' : ''}`} aria-hidden>
              <Icon name="bookmark" variant={isSaved ? 'solid' : 'outline'} useMaskForColor className="hit-card-v2__icon" />
            </div>
            <div className="hit-card-v2__icon-btn" aria-hidden>
              <ShareNetwork className="hit-card-v2__icon h-3.5 w-3.5" />
            </div>
            <div className="hit-card-v2__icon-btn" aria-hidden>
              <Icon name="ellipsisVertical" useMaskForColor className="hit-card-v2__icon" />
            </div>
          </div>
        </div>

        <div className="hit-card-v2__title-block">
          <h3 id={props.titleId} className="sr-only">{props.jobTitle}</h3>
          <Link href={props.jobUrl} className="hit-card-v2__title">
            {fixMojibake(props.jobTitle)}
          </Link>
          {props.rawTitleSubtitle ? (
            <div className="hit-card-v2__subtitle">{fixMojibake(props.rawTitleSubtitle)}</div>
          ) : null}
          {applicationStateRow}
        </div>

        <div className="hit-card-v2__salary-row">{salaryChip}</div>

        {metaFields.length > 0 ? (
          <div className="hit-card-v2__meta">
            {metaFields.map((field, i) => (
              <React.Fragment key={field.key}>
                {i > 0 ? <span aria-hidden className="hit-card-v2__meta-sep">·</span> : null}
                <span className="hit-card-v2__meta-item">{field.label}</span>
              </React.Fragment>
            ))}
          </div>
        ) : null}

        <Link href={props.jobUrl} className="hit-card-v2__cta">
          {strings.viewJob}
        </Link>
      </div>
    </article>
  );
}
